import { Indexable } from '../interfaces';
import { Codes } from './code';

  /** Answers to question about having an accountant. */
  export enum AccountantInfoStatus {
  /** Has an accountant now */
  HasAccountant = 0,
  /** Does not have an accountant */
  HasNoAccountant = 1,
  /** Maybe interested later */
  MayHaveOneLater = 2
}

/** Account status (in the Company.status)*/
export enum AccountStatus {
  /** User is actively entering data. */
  Open = 'Open',

  /**
   * Awaiting signed docs after user reviewed, validated, and submitted for payroll.
   * User cannot make more changes.
   * Operations can.
   */
  Docs = 'Docs',

  /**
   * Operations is working to get this account enrolled after user
   * completed his/her part and signed the docs.
   * Operations may make changes.
   */
  Operations = 'Operations',

  /** DO NOT TOUCH! The system is in the middle of automatically migrating this account to payroll. */
  Migrating = 'Migrating',

  /** CLOSED! Account is enrolled in the payroll system.*/
  Enrolled = 'Enrolled',

  /**
   * Test account, still active as determined by operations.
   * Can still edit and even sign docs but will never be migrated to payroll.
   */
  Test = 'Test',

  /**
   * Abandoned by the user, as determined by operations.
   * No more activity allowed.
   * All users locked out. Admin must re-open to change.
   * Will never be migrated to payroll in this state.
   */
  Abandoned = 'Abandoned',

  /**
   * The account is being re-entered in the Old Enrollment system.
   * No more activity allowed.
   * Should look to the user as if the account is being processed.
   * Will never be migrated to payroll in this state.
   */
  Oz = 'Oz',

  /**
   * The account is blocked.
   * No more activity allowed.
   * Should look to the user as if the account is being processed.
   * Will never be migrated to payroll in this state.
   */
  Fraud = 'Fraud'
}

/** AccountStatus enum codes */
export const AccountStatusCodes = new Codes([
  // Order matters for select options.
  // Reflects the primary workflow.
  { displayName: 'Open', value: AccountStatus.Open },
  { displayName: 'Docs', value: AccountStatus.Docs },
  { displayName: 'Operations', value: AccountStatus.Operations },
  { displayName: 'Migrating', value: AccountStatus.Migrating },
  { displayName: 'Enrolled', value: AccountStatus.Enrolled },
  { displayName: 'Abandoned', value: AccountStatus.Abandoned },
  { displayName: 'Oz', value: AccountStatus.Oz },
  { displayName: 'Fraud', value: AccountStatus.Fraud },
  { displayName: 'Test', value: AccountStatus.Test }
]);

export enum CalendarState {
  Disabled = '',
  PeriodStart = 'from',
  PeriodEnd = 'to',
  Check = 'check',
  SecondPeriodStart = 'secondFrom',
  SecondPeriodEnd = 'secondTo',
  SecondCheck = 'secondCheck'
}

export enum CompanyDeductionMatchType {
  NoMatch = 'NOMATCH',
  SingleTier = 'SINGLE',
  TwoTier = 'TWOTIER'
}

export enum CompanyType {
  Corporation = 'Corporation',
  GeneralPartnership = 'General Partnership',
  LLC = 'Limited Liability Company',
  LimitedPartnership = 'Limited Partnership',
  SoleProprietorship = 'Sole Proprietorship',
  Household = 'Household',
  NonProfit = 'Non-Profit'
}

export const CompanyTypeCodes = new Codes([
  { displayName: 'Corporation', value: CompanyType.Corporation },
  { displayName: 'General Partnership', value: CompanyType.GeneralPartnership },
  { displayName: 'Limited Liability Company', value: CompanyType.LLC },
  { displayName: 'Limited Partnership', value: CompanyType.LimitedPartnership },
  { displayName: 'Sole Proprietorship', value: CompanyType.SoleProprietorship },
  { displayName: 'Household', value: CompanyType.Household },
  { displayName: 'Non-Profit', value: CompanyType.NonProfit }
]);

export const CompanyTitles = {
  [CompanyType.Corporation]: ['Officer', 'Owner', 'Principal', 'Accountant', 'Enrolled Agent'],
  [CompanyType.GeneralPartnership]: [
    'Partner',
    'Owner',
    'Principal',
    'Accountant',
    'Enrolled Agent'
  ],
  [CompanyType.LLC]: ['Member', 'LLC Member', 'Principal', 'Accountant', 'Enrolled Agent'],
  [CompanyType.LimitedPartnership]: [
    'Partner',
    'Limited Partner',
    'Principal',
    'Accountant',
    'Enrolled Agent'
  ],
  [CompanyType.SoleProprietorship]: [
    'Owner',
    'Officer',
    'Principal',
    'Accountant',
    'Enrolled Agent'
  ],
  [CompanyType.Household]: ['Owner', 'Power of Attorney', 'Accountant', 'Enrolled Agent'],
  [CompanyType.NonProfit]: [
    'Partner',
    'Owner',
    'Officer',
    'Executive Director',
    'Principal',
    'Accountant',
    'Enrolled Agent'
  ]
};

/** CompanyTitles that are associated with an Accountant who might sign on the company's behalf. */
export const CompanyAccountantTitles = ['Accountant', 'CPA', 'Enrolled Agent'];

export enum DeductionCodeGroupId {
  RetirementPlans = 1,
  PreTaxInsurancePremiums = 2,
  PostTaxInsurancePremiums = 3,
  FSA = 4,
  OtherPostTax = 5,
  RetirementCatchUpPlans = 6,
  HSA = 7
}

export enum DeductionCodeTypeId {
  SafeHRoth401kMatch = 7,
  SafeHRoth401kNE = 8,
  Roth401kTiered = 9,
  Plan401k = 10,
  Plan401kTiered = 11,
  Plan403b = 12,
  SimpleIRA = 13, // ??
  Roth401k = 14,
  SafeHarbor401k = 15, // SH 401k PRE
  Simple401k = 16, // Simple 401k
  SepIRA = 17,
  SafeHarbor401kNE = 18, // SK 401 PRE NE
  Simple401kNE = 19, // Simple 401k NE
  MedicalPreTax = 20,
  DentalPreTax = 21,
  VisionPreTax = 22,
  MiscellaneousInsurancePremiumPreTax = 23,
  Cafe2 = 24,
  MedicalPostTax = 30,
  DentalPostTax = 31,
  VisionPostTax = 32,
  LifeInsurance = 33,
  MiscellaneousInsurancePremiumPostTax = 34,
  DependentCare = 40,
  Transportation = 41,
  HSAPostTaxContribution1 = 43,
  ChildSupport = 50,
  GarnishmentWithGoal = 51,
  LoanRepayment401k = 52,
  UnionDues = 53,
  RepayCashAdvance = 54,
  RepayLoan = 55,
  CustomWithGoal = 56,
  CustomNoGoal = 57,
  GarnishmentNoGoal = 59,
  Plan401kCatchUp = 60,
  Plan401kTieredCatchUp = 61,
  Plan403BCatchUp = 62,
  SimpleIraCatchUp = 63,
  Roth401kCatchUp = 64,
  SafeH401kCatchUp = 65,
  Roth401kTieredCatchUp = 67,
  SafeHRoth401kNECatchUp = 68,
  SafeH401kMatchCatchUp = 69,
  SafeHRoth401kMatchCatchUp = 70,
  Simple401kCatchUp = 71,
  HSAPreTaxContribution1 = 74,
  HSAPostTaxContribution2 = 75,
  HSAPreTaxContribution2 = 76,
  Meals = 89,
  Parking = 90,
  FSAMedical = 91,
  Garnishment = 92
}

export enum DeductionTaxType {
  PreTax = 'PRETAX',
  PostTax = 'POSTTAX',
  NoEEDeduct = 'NOEEDEDUCT'
}

/** Deposit Frequency Types: keys encoded as expected server-side (do not change). */
export enum DepositFrequencyType {
  NextDay = 'N',
  SemiWeekly = 'I',
  Weekly = 'W',
  EighthMonthly = 'E',
  SemiMonthly = 'S',
  Monthly = 'M',
  SplitMonthly = 'P',
  Quarterly = 'Q',
  Annually = 'A',
  Unassigned = 'U',
  FollowFederal = 'F'
}

export enum EarningsCategory {
  DefaultEarnings = 'DEFAULTEARNINGS',
  DefaultOtherCompensation = 'DEFAULTOTHERCOMPENSATION',
  AdditionalEarnings = 'ADDITIONALEARNINGS'
}

/** How the EE is paid: hourly, salary, unit, mile */
export enum EmployeePayType {
  Hourly = 'hourly',
  Mile = 'mile',
  Salary = 'salary',
  Unit = 'unit'
}

/** Active, New Hire, Terminated. A synthetic type, constructed from EE.isTerminated and EE.isNewHire flags. */
export enum EmployeeStatus {
  Active = 'active',
  NewHire = 'newhire',
  Terminated = 'terminated'
}

/** Ecommerce Employee Types */
export enum EmployeeType {
  Contractor = 'contractor', // '1' (1099 Contractor) in Product
  FullTime = 'fulltime', // 'F' in Product
  PartTime = 'parttime' // 'P' in Product
}

/** SP Employee Type values. Only for EntityChange imports. See EntityChangeEmployeeInfo interface. */
export type EmployeeTypeSP = '1' | 'F' | 'H' | 'O' | 'P' | 'T';

/** SP Employee Type codes */
export const EmployeeTypeSpCodes = new Codes([
  { value: '1', displayName: '1099 Contractor' },
  { value: 'F', displayName: 'Full Time' },
  { value: 'H', displayName: 'Household' },
  { value: 'O', displayName: 'Officer' },
  { value: 'P', displayName: 'Part Time' },
  { value: 'T', displayName: 'Temporary' }
]);

/** Map of SP Employee Type codes to ECommerce Employee Types */
export const EmployeeTypeSpMap: Indexable<string> = {
  1: EmployeeType.Contractor,
  F: EmployeeType.FullTime,
  P: EmployeeType.PartTime
};

/** From DocuSign */
export enum EnvelopeStatus {
  /** Created locally, not pushed to DocuSign */
  local = 'local',
  /** Pushed to DocuSign in 'draft' status (docs and recipients can still be added) */
  draft = 'draft',
  /** Pushed to DocuSign in 'sent' status (sent by email OR available for in-app signing) */
  sent = 'sent',
  /** Received? */
  delivered = 'delivered',
  /** Signed */
  completed = 'completed',
  /** Refused to sign */
  declined = 'declined',
  /** Voided in DocuSign */
  voided = 'voided'
}

// from https://developers.docusign.com/esign-rest-api/v2/guides/concepts/recipients
/** From DocuSign */
export enum EnvelopeSignerStatus {
  /** Recipient's email system auto-responded to the email from DocuSign */
  autoresponded = 'autoresponded',
  /** Envelope was voided */
  cancel = 'cancel',
  /** Signed or otherwise completed */
  completed = 'completed',
  /** Created in a 'draft' envelope.  Not used in ECommerce */
  created = 'created',
  /** Refused to sign */
  declined = 'declined',
  /** Recipient has viewed the document */
  delivered = 'delivered',
  /** Created locally, not pushed to DocuSign */
  local = 'local',
  /** Recipient has been sent an email that it is their turn to sign  */
  sent = 'sent',

  // Events that we receive from DocuSign and store in EnvelopeSigner.status

  /** Session timed out before the user signed */
  session_timeout = 'session_timeout',
  /** Signed */
  signing_complete = 'signing_complete',
  /** Session timed out before the user signed */
  ttl_expired = 'ttl_expired',
  /** Recipient has viewed the documents */
  viewing_complete = 'viewing_complete'
}

export enum FeinStatus {
  /** The FEIN number is intrinsically invalid */
  Invalid = 'invalid',
  /** The entered FEIN has been verified and matches the Legal Name */
  Verified = 'verified',
  /**  Well-formed FEIN has been entered but is not verified */
  Unverified = 'unverified'
}

export enum GarnishmentFrequency {
  Weekly = '1w',
  Every2Weeks = '2w',
  TwiceAMonth = '0.5m',
  Monthly = '1m'
}

export enum HouseholdFilersStatus {
  Single = 'single', // 'Single / Head of Household'
  Joint = 'joint'
}

export enum HsaMatchType {
  NoMatch = 'NOMATCH',
  Match = 'MATCH',
  MatchAll = 'MATCHALL'
}

export enum HsaMaxContributionType {
  Individual = 'INDIVIDUAL',
  Family = 'FAMILY'
}

export enum ImportType {
  EntityChange = 'ENTITY_CHANGE',
  NannyChex = 'NANNYCHEX'
}

export const ImportTypeCodes = new Codes([
  { displayName: 'Entity Change', value: ImportType.EntityChange },
  { displayName: 'NannyChex', value: ImportType.NannyChex }
]);

/** States that do not have an income tax (no SIT) */
export const noIncomeTaxStates = ['AK', 'FL', 'NH', 'SD', 'NV', 'TN', 'TX', 'WA', 'WY'];

/** EE Payment account type, e.g, 'Checking' */
export enum PaymentAccountType {
  Checking = 'Checking',
  Savings = 'Savings',
  PrintedCheck = 'Printed Check'
}

/** Whether EE is paid as a single amount, split by $, or split by % */
export enum PaymentStrategy {
  SinglePayment = 'single-payment',
  SplitAmount = 'split-amount',
  SplitPercent = 'split-percent'
}

/** Whether EE is paid entirely by check or by deposit or both */
export enum PaymentMethod {
  PrintedCheck = 'paper-check',
  DepositOrPaycard = 'deposit-or-paycard',
  Both = 'both'
}

export enum PayrollFrequency {
  Weekly = 'Weekly',
  BiWeekly = 'Bi-Weekly',
  SemiMonthly = 'Semi-Monthly',
  Monthly = 'Monthly',
  Quarterly = 'Quarterly',
  Annual = 'Annual'
}

export const PayrollFrequencyCodes = new Codes([
  // Order matters for select options.
  { displayName: 'Weekly', value: 'Weekly' },
  { displayName: 'Bi-Weekly (Every Other Week)', value: 'Bi-Weekly' },
  { displayName: 'Semi-Monthly (Twice a Month)', value: 'Semi-Monthly' },
  { displayName: 'Monthly', value: 'Monthly' }
  // { displayName: 'Quarterly', value: 'Quarterly' } // removed temporarily PKSP-20550
]);

export enum RetirementPlan {
  IRA = 'IRA',
  Plan401K = '401K',
  Plan403B = '403B'
}

export enum RetirementPlanType {
  P401KTraditional = '401KTraditional',
  P401KSafeHarbor = '401KSafeHarbor',
  P401KSimple = '401KSimple',
  SimpleIRA = 'SimpleIRA',
  SepIRA = 'SepIRA'
}

export enum RetirementTaxMode {
  PreTax = 'PreTax',
  PostTax = 'PostTax'
}

export enum SectionStatus {
  NotStarted = 'NotStarted',
  Invalid = 'Invalid',
  Valid = 'Valid'
}

export enum SeinStatus {
  AppliedFor = 'applied-for',
  Unverified = 'unverified',
  Invalid = 'invalid',
  NA = 'na',
  Verified = 'verified',
  UseFein = 'use-fein'
}

export enum Sex {
  Female = 'F',
  Male = 'M'
}

export const SexCodes = new Codes([
  { displayName: 'Female', value: Sex.Female },
  { displayName: 'Male', value: Sex.Male }
]);

export enum SuiStatus {
  AppliedFor = 'applied-for',
  Unverified = 'unverified',
  Invalid = 'invalid',
  Verified = 'verified'
}

export enum TaxCategoryCode {
  FederalWH = 'FEDWT',
  FutaER = 'FUTAER',
  StateWH = 'STATEWT'
}

export enum TaxExemptionReason {
  InsuranceAgentsOrSolicitors = 'AGRIC',
  AgriculturalLabor = 'AGRIC',
  DomesticServiceInCollegeClubs = 'CLUB',
  DeceasedWorkersWages = 'DECEAS',
  EmployersWhoseTaxabilityDependsUponNumberOfEmployees = 'EMPNO',
  ForeignWorkersPerformingTemporaryAgriculturalLabor = 'FAGRI',
  FamilyEmployees = 'FAMILY',
  FederalEmployees = 'FEDEMP',
  FishingAndRelatedActivities = 'FISH',
  ForeignServiceByUSCitizens = 'FORCIT',
  HouseholdWorkers = 'HWORK',
  IndustrialHomeworkers = 'INDUST',
  ForeignGovernmentAndInternationalOrganizations = 'INTER',
  InternsWorkingInHospitals = 'INTERN',
  Ministers = 'MINIST',
  NewspaperCarriersUnder18 = 'NEW18',
  NewspaperAndMagazineVendors = 'NEWSV',
  MembersOfReligiousOrdersWhoveNotTakenVowOfPoverty = 'NPOV',
  NonProfitOrganizations = 'NPROFIT',
  PatientsEmployedByHospitals = 'PATIENT',
  Railroads = 'RAIL',
  RealEstateAgentsAndDirectSellers = 'REAL',
  ReligiousExemption = 'RELIG',
  SicknessOrInjuryPayments = 'SICKPAY',
  EmployeesOfStateGovernmentsAndPoliticalSubdivisions = 'STATE',
  Students = 'STUDENT',
  NonResidentAlienFJMVisa = 'VISA',
  MembersOfReligiousOrdersWhoHaveTakenAVowOfPoverty = 'VPOV'
}

export const TaxExemptionReasonsCodes = new Codes([
  // Order matters for select options.
  {
    displayName: 'Insurance agents or solicitors',
    value: TaxExemptionReason.InsuranceAgentsOrSolicitors
  },
  { displayName: 'Agricultural labor', value: TaxExemptionReason.AgriculturalLabor },
  {
    displayName: 'Domestic service in college clubs',
    value: TaxExemptionReason.DomesticServiceInCollegeClubs
  },
  { displayName: "Deceased worker's wages", value: TaxExemptionReason.DeceasedWorkersWages },
  {
    displayName: 'Employers whose taxability depends upon number of employees',
    value: TaxExemptionReason.EmployersWhoseTaxabilityDependsUponNumberOfEmployees
  },
  {
    displayName: 'Foreign workers performing temporary agricultural labor',
    value: TaxExemptionReason.ForeignWorkersPerformingTemporaryAgriculturalLabor
  },
  { displayName: 'Family employees', value: TaxExemptionReason.FamilyEmployees },
  { displayName: 'Federal employees', value: TaxExemptionReason.FederalEmployees },
  {
    displayName: 'Fishing and related activities',
    value: TaxExemptionReason.FishingAndRelatedActivities
  },
  {
    displayName: 'Foreign service by US citizens',
    value: TaxExemptionReason.ForeignServiceByUSCitizens
  },
  { displayName: 'Household workers', value: TaxExemptionReason.HouseholdWorkers },
  { displayName: 'Industrial homeworkers', value: TaxExemptionReason.IndustrialHomeworkers },
  {
    displayName: 'Foreign government and International organizations',
    value: TaxExemptionReason.ForeignGovernmentAndInternationalOrganizations
  },
  {
    displayName: 'Interns working in hospitals',
    value: TaxExemptionReason.InternsWorkingInHospitals
  },
  { displayName: 'Ministers', value: TaxExemptionReason.Ministers },
  {
    displayName: 'Newspaper carriers under 18',
    value: TaxExemptionReason.NewspaperCarriersUnder18
  },
  {
    displayName: 'Newspaper and magazine vendors',
    value: TaxExemptionReason.NewspaperAndMagazineVendors
  },
  {
    displayName: "Members of religious orders who've not taken vow of poverty",
    value: TaxExemptionReason.MembersOfReligiousOrdersWhoveNotTakenVowOfPoverty
  },
  { displayName: 'Non-profit organizations', value: TaxExemptionReason.NonProfitOrganizations },
  {
    displayName: 'Patients employed by hospitals',
    value: TaxExemptionReason.PatientsEmployedByHospitals
  },
  { displayName: 'Railroads', value: TaxExemptionReason.Railroads },
  {
    displayName: 'Real estate agents and direct sellers',
    value: TaxExemptionReason.RealEstateAgentsAndDirectSellers
  },
  { displayName: 'Religious Exemption', value: TaxExemptionReason.ReligiousExemption },
  {
    displayName: 'Sickness or injury payments',
    value: TaxExemptionReason.SicknessOrInjuryPayments
  },
  {
    displayName: 'Employees of state governments and political subdivisions',
    value: TaxExemptionReason.EmployeesOfStateGovernmentsAndPoliticalSubdivisions
  },
  { displayName: 'Students', value: TaxExemptionReason.Students },
  {
    displayName: 'Non-Resident Alien F/J/M Visa',
    value: TaxExemptionReason.NonResidentAlienFJMVisa
  },
  {
    displayName: 'Members of religious orders who have taken a vow of poverty',
    value: TaxExemptionReason.MembersOfReligiousOrdersWhoHaveTakenAVowOfPoverty
  }
]);

/** Filing status codes that could be used in any Fed or State withholding tax code */
export const TaxFilingStatusCodes = new Codes([
  { value: 'S', displayName: 'Single' },
  { value: 'M', displayName: 'Married' },
  { value: 'H', displayName: 'Head of Household' },
  { value: 'I', displayName: 'Single or Married, Filing Separately' },
  { value: 'J', displayName: 'Married, Filing Jointly' },
  { value: 'K', displayName: 'Head of Household' }
]);

/** Tax Rate type per SPTaxCodes. Whether the tax rate has a "Default" (AKA Fixed) or Variable value */
export type TaxRateType  = 'Default' | 'Variable';

export enum TaxTypeCode {
  Federal = 'FD',
  State = 'ST',
  County = 'CN',
  City = 'CT',
  SchoolDistrict = 'SD'
}

export enum TipsEarningCode {
  CreditCardOwed = '66',
  TipsToMinimum = '27',
  CreditCardPaid = '25',
  TipsCash = '63'
}

export enum UploadedFileDocumentType {
  /** Unknown document type */
  Unspecified = 0,
  /** Prior-to-Service Wages document */
  PTSWages = 1,
  /** Contact-Us Document */
  ContactUs = 2,
  /** Website Bank Account Change Request Document */
  BankAccount = 3
}

export enum UploadedFileStatus {
  /** File created but not scanned */
  Created = 1,
  /** File is uploading */
  Uploading = 2,
  /** Scanning uploaded file for viruses */
  Processing = 3,
  /** File was rejected by Virus Scan or other means */
  Rejected = 4,
  /** File completed virus scan and accepted */
  Completed = 5
}

/** ECommerce User Roles - not to be confused with roles in SP OIDC user tokens */
export enum UserRole {
  /** SP ECommerce administrator with full access to all companies */
  Administrator = 'Administrator',

  /**
   * SurePayroll Partner/Reseller empowered to enroll a company, such as a CPA.
   * A partner could access multiple companies.
   */
  Partner = 'Partner',

  /**
   * An employee of a Partner, with most of the same privileges
   */
  PartnerAuthorized = 'PartnerAuthorized',

  /**
   * A user who can fully enroll a single company.
   * Not necessarily the principal/signer.
   */
  Approver = 'Approver'
}

/** Known partner codes */
export enum WellKnownPartnerCodes {
  /** NannyChex Self-Service import account */
  NannyChexSelfServiceImport = 'NCSS',
  /** SP's program code */
  /** NannyChex White Glove import account (deprecated) */
  NannyChexWhiteGloveImport = 'NCXM',
  Sure = 'SURE',
}

/** Known program codes */
export enum WellKnownProgramCodes {
  /**  Paychex's program code */
  Pxgo = 'PXGO',
  /** SP's program code */
  Sure = 'SURE',
  /** Trusted Advisor Program (Partner/Reseller) */
  Tap = 'TAP'
}

/** Type that constrains EmployeeTax.w4FormVersion to specific W4 version values */
export type W4FormVersion = '' | '2019' | '2020';

/* Registration Get Start Page options for select */
export enum WhereDidYouHearCodes {
  WordOfMouth = 'WordOfMouth',
  SocialMedia = 'SocialMedia',
  MyAccountant = 'MyAccountant',
  TvRadio = 'TvRadio',
  Search = 'Search',
  MyBanker = 'MyBanker',
  OnlineReviews = 'OnlineReviews',
  Other='Other'
};

export const WhereDidYouHearOptions = new Codes([
  { displayName: 'Word Of Mouth', value: WhereDidYouHearCodes.WordOfMouth },
  { displayName: 'Social Media', value: WhereDidYouHearCodes.SocialMedia },
  { displayName: 'My Accountant', value: WhereDidYouHearCodes.MyAccountant },
  { displayName: 'Tv/Radio', value: WhereDidYouHearCodes.TvRadio },
  { displayName: 'Search', value: WhereDidYouHearCodes.Search },
  { displayName: 'My Banker', value: WhereDidYouHearCodes.MyBanker },
  { displayName: 'Online Reviews', value: WhereDidYouHearCodes.OnlineReviews },
  { displayName: 'Other', value: WhereDidYouHearCodes.Other }
]);